<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue';

import ButtonAddToCart from '@/components/CartActions/ButtonAddToCart.vue';
import ButtonText from '@/shared/components/UI/Buttons/ButtonText.vue';
import Modal from '@/shared/components/UI/Modal.vue';
import { PatternProduct } from '@/shared/composables/patternProducts/patternProducts.types';
import { usePatternProducts } from '@/shared/composables/patternProducts/usePatternProducts';
import { useTranslations } from '@/shared/composables/useTranslations';
import { productTypes } from '@/shared/constants/shopify.ts';
import { useCartStore } from '@/shared/pinia/modules/cart';
import { trackPatternProductsAddAllToCart } from '@/shared/tracking/pattern-products';
import { getErrorMessage } from '@/shared/utils/error';
import { formatPriceStorefront } from '@/shared/utils/shopify/money-formatter';
import {
  trackHobbiiEvent,
  UserEventDefinitions
} from '@/shared/utils/tracking';

import CollapsibleContainer from './CollapsibleContainer.vue';
import PatternProductCard from './PatternProductCard.vue';
import PatternProductSkeleton from './PatternProductSkeleton.vue';
import ProductsContainer from './ProductsContainer.vue';

const { t } = useTranslations();
const cartStore = useCartStore();
const buttonAddAllToCartRef = useTemplateRef('ButtonAddAllToCart');

const size = ref<string | null | undefined>(null);
const isAddingAllToCart = ref<boolean>(false);
const displayWarning = ref<boolean>(false);
const productCardsRef = ref<(typeof PatternProductCard)[]>([]);
const showingOnlyYarn = ref<boolean>(false);

const { patternId, defaultPatternProductsList } = defineProps<{
  patternId: string;
  defaultPatternProductsList: PatternProduct[];
}>();

const {
  patternProductsLoading,
  incompletePatternProducts,
  patternSizes,
  handleSizeChange,
  visiblePatternProducts,
  updateQuantityByDisplayIndex,
  updateVariantByDisplayIndex
} = usePatternProducts(patternId, defaultPatternProductsList, showingOnlyYarn);

const totalPrice = computed(() => {
  return visiblePatternProducts.value.reduce((total, vpp) => {
    const inStock = (vpp.product?.variant?.quantityAvailable || 0) > 0;
    if (inStock) {
      const unitPrice = vpp.product ? vpp.product.price.amount : 0;
      total = total + unitPrice * vpp.quantity;
    }
    return total;
  }, 0);
});

const totalPriceFormatted = computed(() => {
  return formatPriceStorefront({
    amount: totalPrice.value,
    currencyCode: window.Shopify.currency.active
  });
});

const addAllToCartLabel = t('patterns.products.add-all-to-cart-label');

const sizeOptions = computed(() => {
  return patternSizes.value
    .filter((a) => a)
    .map((ps) => {
      return { value: ps.title || '', label: ps.title || '' };
    });
});

const isThereAnyInsufficientStock = computed(() => {
  return visiblePatternProducts.value.reduce(
    (anyInsufficientStock, patternProduct) => {
      return (
        anyInsufficientStock ||
        (patternProduct.product?.variant?.quantityAvailable || 0) <
          patternProduct.quantity
      );
    },
    false
  );
});

const isEverythingOutOfStock = computed(() => {
  return visiblePatternProducts.value.reduce(
    (allOutOfStock, patternProduct) =>
      allOutOfStock &&
      (patternProduct.product?.variant?.quantityAvailable || 0) <= 0,
    true
  );
});

const hasSelectedItems = computed(() => {
  return visiblePatternProducts.value.some(
    (p) => p.quantity > 0 && (p.product?.variant?.quantityAvailable || 0) > 0
  );
});

watch(patternSizes, (newVal) => {
  if (newVal.length) {
    size.value = newVal[0].title;
  }
});

const onProductVariantChange = ({
  productId,
  variantId,
  index
}: {
  productId: string;
  variantId: string;
  index: number;
}) => {
  updateVariantByDisplayIndex(index, productId, variantId);
};

const onProductQuantityChange = ({
  quantity,
  index
}: {
  productId: string;
  variantId: string;
  quantity: number;
  index: number;
}) => {
  updateQuantityByDisplayIndex(index, quantity);
};

const trackAddAllToCart = (
  items: UserEventDefinitions['pattern_products_add_all_to_cart']['items']
) => {
  // Existing GTM/GA tracking
  trackHobbiiEvent('pattern_products_add_all_to_cart', {
    currency: window.Shopify.currency.active,
    items,
    value: items.reduce(
      (sum, item) => sum + item.price * (item.quantity || 0),
      0
    ),
    category: 'all_recommendations',
    label: patternId
  });

  // PostHog tracking for what_youll_need_add_all_to_cart

  const posthogTrackingProducts = items.map((item) => ({
    product_id: item.item_id,
    product_name: item.item_name,
    product_category: item.item_category,
    variant: item.item_variant,
    quantity: item.quantity,
    price: item.price
  }));

  trackPatternProductsAddAllToCart(
    posthogTrackingProducts,
    'show_everything_by_default'
  );
};

const addAllToCart = async () => {
  isAddingAllToCart.value = true;

  try {
    const items = productCardsRef.value
      .map((product) => product.getCartItem())
      .filter((a) => a);

    if (items.length === 0) {
      return;
    }

    await cartStore.addIndependentProductsToCart(items);

    trackAddAllToCart(
      productCardsRef.value
        .map((product) => product.getTrackingItem())
        .filter((a) => a)
    );
  } catch (err) {
    const errorMessage = getErrorMessage(err);

    if (errorMessage) {
      buttonAddAllToCartRef?.value?.displayError(errorMessage);
    }
  } finally {
    isAddingAllToCart.value = false;
    displayWarning.value = false;
  }
};

const handleAddAllToCartClick = async () => {
  if (isAddingAllToCart.value) return;
  if (isThereAnyInsufficientStock.value) {
    displayWarning.value = true;
    return;
  }

  addAllToCart();
};

onMounted(() => {
  trackHobbiiEvent('feature_view', {
    category: 'section',
    item_name: 'pattern_product_suggestions',
    label: 'Pattern Products'
  });
});

const yarnProducts = computed(() =>
  visiblePatternProducts.value
    .map((patternProduct, index) => ({ patternProduct, index }))
    .filter(
      ({ patternProduct }) =>
        patternProduct.product?.productType === productTypes.YARN
    )
);

const accessoryProducts = computed(() =>
  visiblePatternProducts.value
    .map((patternProduct, index) => ({ patternProduct, index }))
    .filter(
      ({ patternProduct }) =>
        patternProduct.product?.productType !== productTypes.YARN
    )
);
</script>

<template>
  <Transition name="fade" mode="out-in">
    <div
      v-if="!patternProductsLoading"
      class="mt-4 rounded-lg border px-2 pt-2 md:px-4 md:pt-4"
    >
      <h2 class="mt-0 text-xl font-bold text-black">
        {{ t('patterns.products.find_your_materials') }}
      </h2>
      <div
        v-if="incompletePatternProducts"
        class="mt-4 inline-flex w-full rounded-xl bg-yellow-400 px-4 py-2 text-sm text-yellow-950"
      >
        {{ t('patterns.products.incomplete-pattern-products') }}
      </div>

      <div v-if="visiblePatternProducts.length > 0" class="flex flex-col">
        <CollapsibleContainer :products-count="visiblePatternProducts.length">
          <ProductsContainer>
            <template #number>1</template>
            <template #header>
              {{ t('patterns.products.choose-size') }}
            </template>
            <template #default>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="sizeOption in sizeOptions"
                  :key="sizeOption.value"
                  class="rounded-lg border px-2 py-1 text-xs font-normal text-black transition-colors hover:border-gray-400"
                  :class="
                    size === sizeOption.value
                      ? '!border-black bg-black text-white'
                      : 'bg-white'
                  "
                  @click="
                    size = sizeOption.value;
                    handleSizeChange(sizeOption.value);
                  "
                >
                  {{ sizeOption.label }}
                </button>
              </div>
            </template>
          </ProductsContainer>

          <ProductsContainer>
            <template #number>2</template>
            <template #header>
              {{ t('patterns.products.yarn-you-need') }}
            </template>
            <template #default>
              <PatternProductCard
                v-for="item in yarnProducts"
                :key="`yarn-product-${item.patternProduct.variant_id}-${item.index}`"
                ref="productCardsRef"
                :index="item.index"
                :pattern-product="item.patternProduct"
                @variant-change="onProductVariantChange"
                @quantity-change="onProductQuantityChange"
            /></template>
          </ProductsContainer>

          <ProductsContainer>
            <template #number>3</template>
            <template #header>
              {{ t('patterns.products.tools-and-accessories') }}
            </template>
            <template #default>
              <PatternProductCard
                v-for="item in accessoryProducts"
                :key="`accessory-product-${item.patternProduct.variant_id}-${item.index}`"
                ref="productCardsRef"
                :index="item.index"
                :pattern-product="item.patternProduct"
                @variant-change="onProductVariantChange"
                @quantity-change="onProductQuantityChange"
              />
            </template>
          </ProductsContainer>
          <template #footer>
            <div class="mb-2 flex items-end justify-between gap-4 text-black">
              <div class="text-sm leading-none">
                {{ t('product.quantity.text_total_price') }}:
              </div>
              <div class="text-lg font-bold leading-none">
                {{ totalPriceFormatted }}
              </div>
            </div>
            <ButtonAddToCart
              ref="ButtonAddAllToCart"
              :disabled="
                isAddingAllToCart ||
                isEverythingOutOfStock ||
                (visiblePatternProducts.length === 0 &&
                  !patternProductsLoading) ||
                !hasSelectedItems
              "
              :label="addAllToCartLabel"
              @click="handleAddAllToCartClick"
            />
          </template>
        </CollapsibleContainer>
      </div>
    </div>

    <div v-else class="flex flex-col gap-4 overflow-hidden">
      <PatternProductSkeleton v-for="i in 4" :key="`product-skeleton-${i}`" />
    </div>
  </Transition>

  <Modal v-model:active="displayWarning" size="md" :always-on-top="true">
    <div
      class="mx-auto flex min-h-96 w-full max-w-2xl flex-col items-center justify-center gap-8 pt-6"
    >
      <h1 class="text-3xl font-bold">{{ t('global.errors.oh-no') }}</h1>

      <div class="mx-4 text-center text-lg text-gray-500">
        {{ t('patterns.products.error.some-out-of-stock-items') }}
      </div>

      <div class="flex flex-col gap-1">
        <ButtonText
          class="mt-4 text-white"
          theme="primary-pink"
          size="lg"
          :loading="isAddingAllToCart"
          @click="addAllToCart"
        >
          {{ t('patterns.products.error.proceed') }}
        </ButtonText>
        <ButtonText
          class="mt-4 border font-bold"
          theme="white"
          size="lg"
          @click="displayWarning = false"
        >
          {{ t('patterns.products.error.cancel') }}
        </ButtonText>
      </div>
    </div>
  </Modal>
</template>
